/**
 * Labstep
 *
 * @module containers/Entity/Toggle
 * @desc Container for adding/removing children entities
 */

import type {
  APICallOptions,
  APICallStatus,
} from 'labstep-web/models';
import type { EntityName } from 'labstep-web/services/schema/schema';
import {
  addEntity,
  removeEntity,
  setEntity,
} from 'labstep-web/state/actions/entity';
import { selectToggleStatus } from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export interface IEntityToggleContainerProps<
  T extends EntityName = EntityName,
> {
  entityName: T;
  entityIds?: string | (string | number)[] | number;
  parentName: string;
  parentId: string | (string | number)[] | number;
  action?: string;
  childKeyName?: string;
  single?: boolean;
}

export interface IEntityToggleContainerChildrenProps {
  toggle: (
    action?: string,
    ids?: number[],
    options?: APICallOptions,
  ) => void;
  status: APICallStatus;
}

const Container = ({
  children,
  entityName,
  entityIds,
  parentName,
  parentId,
  action,
  childKeyName,
  single,
}: {
  children: (
    props: IEntityToggleContainerChildrenProps,
  ) => React.ReactElement | null;
} & IEntityToggleContainerProps) => {
  const dispatch = useDispatch();

  const status = useSelector((state: any) =>
    entityIds
      ? selectToggleStatus(
          state,
          entityName,
          entityIds,
          parentName,
          parentId,
        )
      : false,
  );

  const toggle = useCallback(
    (
      actionParam?: string,
      ids?: number[],
      options?: APICallOptions,
    ) => {
      // Props get priority
      const actionType = action || actionParam;
      const targetIds = entityIds || ids;

      if (!targetIds) {
        return;
      }

      if (actionType === 'add') {
        dispatch(
          addEntity(entityName, targetIds, parentName, parentId, {
            ...options,
            childKeyName,
          }),
        );
      } else if (actionType === 'remove') {
        dispatch(
          removeEntity(entityName, targetIds, parentName, parentId, {
            ...options,
            childKeyName,
            single,
          }),
        );
      } else if (actionType === 'set') {
        dispatch(
          setEntity(entityName, targetIds, parentName, parentId, {
            ...options,
            childKeyName,
            single,
          }),
        );
      }
    },
    [
      dispatch,
      entityName,
      entityIds,
      parentName,
      parentId,
      action,
      childKeyName,
      single,
    ],
  );

  const props: IEntityToggleContainerChildrenProps = {
    toggle,
    status,
  };

  return children(props);
};

/**
 * Toggle Container connecting a component with state
 * Returns original component with
 * toggle function and status of action
 */
export const EntityToggleContainer = Container;
